import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import PhoneFieldView from '../view/PhoneFieldView';
import { allCountryOptions } from '../../../data/countryCodes';
import { FormContext, useField } from '../FormContext';
import useTranslations from '../../../hooks/useTranslations';
import { isValidPhoneNumber } from 'libphonenumber-js';

const extractCountryCode = (longNumberString, countryOptions) => {
  let lengthToAttempt, testCode, match;
  lengthToAttempt = 6;
  if (!longNumberString) {
    return '';
  }
  while (lengthToAttempt > 1) {
    testCode = longNumberString.substr(0, lengthToAttempt);
    match = countryOptions.find(o => o.dialCode === testCode);
    if (match) {
      return match;
    }
    lengthToAttempt--;
  }
  return '';
};

export const phoneFieldValidator = (t, required) => value => {
  let countryMatch, localNumber;
  if (value) {
    countryMatch = extractCountryCode(value, allCountryOptions);
    if (countryMatch) {
      localNumber = value.substr(countryMatch.dialCode.length);
      if (localNumber.indexOf('0') === 0) {
        return t('forms.fields.error.phone_leading_zero');
      }
    }
  }
  if (
    (required && !value) ||
    (value && !isValidPhoneNumber(value)) ||
    (value && value.indexOf(' ') !== -1)
  ) {
    return t('forms.fields.error.phone');
  }
};

const PhoneFieldEngine = ({
  internalId,
  value,
  setValue = () => {},
  onChange = () => {},
  required,
  readOnly,
  options,
  defaultCountry: givenDefaultCountry = 'gb',
  onFocus: givenOnFocus,
  onBlur: givenOnBlur,
  ...fieldProps // for future development, know whe have this
}) => {
  const t = useTranslations();
  const {
    onFormBuilderDefaultValueSaved = () => {},
    validateField = () => {}
  } = useContext(FormContext);

  const onlyCountries = options && options.length ? options : false;

  const onlyLowerCountries = useMemo(
    () =>
      onlyCountries ? onlyCountries.map(one => one.toLocaleLowerCase()) : false,
    [options]
  );

  const countryOptions = useMemo(
    () =>
      onlyLowerCountries
        ? allCountryOptions.filter(one =>
            onlyLowerCountries.includes(one.code.toLocaleLowerCase())
          )
        : allCountryOptions,
    [allCountryOptions]
  );

  const [country, setCountry] = useState('');
  const [dialCode, setDialcode] = useState('');
  const [localNumber, setLocalNumber] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const defaultCountry = givenDefaultCountry
      ? countryOptions.find(
          one =>
            one.code.toLocaleLowerCase() ===
            givenDefaultCountry.toLocaleLowerCase()
        )
      : null;
    const countryMatch =
      extractCountryCode(value, countryOptions) || defaultCountry;
    setCountry(countryMatch ? countryMatch.text : '');
    setDialcode(countryMatch ? countryMatch.dialCode : '');
    setLocalNumber(
      value && countryMatch ? value.substr(countryMatch.dialCode.length) : ''
    );
    setIsInitialized(true);
  }, [givenDefaultCountry]);

  useEffect(() => {
    // nothing if no local number
    const cleanValue =
      localNumber && dialCode
        ? dialCode + localNumber.replace(/[^\d.]+/g, '')
        : localNumber
        ? '+ ' + localNumber.replace(/[^\d.]+/g, '')
        : '';
    if (value === cleanValue || (!value && !cleanValue)) {
      return;
    }
    if (isInitialized) {
      onChange(cleanValue);
    }
    setValue(cleanValue);
    onFormBuilderDefaultValueSaved(cleanValue);
  }, [dialCode, localNumber]);

  const handleLocalNumberChange = useCallback(
    e => {
      const val = e.target.value;
      const cleanVal = val.replace(/[^\d.]+/g, '');
      setLocalNumber(cleanVal);
    },
    [dialCode]
  );

  const handleChangeCountry = useCallback(
    val => {
      const matchingCountry = countryOptions.find(one => one.value === val);
      if (matchingCountry) {
        setDialcode(matchingCountry.dialCode);
      } else {
        setDialcode('');
      }
      setCountry(val);
      setTimeout(() => {
        validateField(internalId);
      }, 100);
    },
    [localNumber]
  );

  const asyncCountryOptions = searchValue => {
    return new Promise((resolve, reject) => {
      try {
        if (searchValue) {
          return resolve(
            countryOptions.filter(one =>
              one.displayString
                .toLocaleLowerCase()
                .includes(searchValue.toLocaleLowerCase())
            )
          );
        }
        return resolve(countryOptions);
      } catch (e) {
        reject(e);
      }
    });
  };

  const openMenuOptionParser = useCallback(
    optionObject => {
      const matchingCountry = countryOptions.find(
        one => one.value === optionObject.value
      );
      return `${matchingCountry.displayString}`;
    },
    [countryOptions]
  );

  useField(internalId, phoneFieldValidator(t, required));

  const onBlur = useCallback(() => {
    validateField(internalId);
  }, [value]);

  const viewProps = {
    asyncCountryOptions,
    country,
    handleChangeCountry,
    handleLocalNumberChange,
    localNumber,
    onBlur: givenOnBlur || onBlur,
    onFocus: givenOnFocus,
    openMenuOptionParser,
    readOnly
  };

  return <PhoneFieldView {...viewProps} />;
};

export default PhoneFieldEngine;
